// Phase 18 · Community · Vote buttons (Reddit-style up/down)
import React, { useState } from "react";
import { ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VoteButtons({ targetType = "post", targetId, initial, onScoreChange, size = "md" }) {
    const [state, setState] = useState({
        upvotes: initial?.upvotes || 0,
        downvotes: initial?.downvotes || 0,
        score: initial?.score || 0,
        direction: "none",
    });
    const [busy, setBusy] = useState(false);

    const cast = async (direction) => {
        if (busy) return;
        setBusy(true);
        try {
            const resp = await fetch(`${API}/api/community/${targetType === "post" ? "posts" : "comments"}/${targetId}/vote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ direction }),
            });
            if (!resp.ok) {
                if (resp.status === 401) {
                    toast.error("Login required to vote");
                } else if (resp.status === 429) {
                    toast.error("Too many votes — slow down");
                } else {
                    toast.error("Vote failed");
                }
                return;
            }
            const data = await resp.json();
            setState({
                upvotes: data.upvotes,
                downvotes: data.downvotes,
                score: data.score,
                direction: data.direction,
            });
            onScoreChange?.(data.score);
        } catch (e) {
            toast.error("Network error");
        } finally {
            setBusy(false);
        }
    };

    const sz = size === "sm" ? 16 : 22;
    const upActive = state.direction === "up";
    const downActive = state.direction === "down";

    return (
        <div
            className="flex flex-col items-center gap-0.5 select-none"
            data-testid={`vote-cluster-${targetType}-${targetId}`}
        >
            <button
                type="button"
                onClick={() => cast("up")}
                disabled={busy}
                className={`transition-colors ${upActive ? "text-white" : "text-[var(--color-text-dim)] hover:text-white"}`}
                aria-label="Upvote"
                data-testid={`vote-up-${targetType}-${targetId}`}
            >
                <ArrowUpCircle size={sz} strokeWidth={upActive ? 2.5 : 1.75} />
            </button>
            <span
                className={`text-xs font-mono tabular-nums ${state.score > 0 ? "text-white" : state.score < 0 ? "text-[var(--color-alert)]" : "text-[var(--color-text-dim)]"}`}
                data-testid={`vote-score-${targetType}-${targetId}`}
            >
                {state.score}
            </span>
            <button
                type="button"
                onClick={() => cast("down")}
                disabled={busy}
                className={`transition-colors ${downActive ? "text-neutral-100" : "text-neutral-500 hover:text-neutral-200"}`}
                aria-label="Downvote"
                data-testid={`vote-down-${targetType}-${targetId}`}
            >
                <ArrowDownCircle size={sz} strokeWidth={downActive ? 2.5 : 1.75} />
            </button>
        </div>
    );
}
